/**
 * Hook para gerenciar dados da Waiting List V2.
 * Carrega lista de pacientes e expõe ações (CRUD).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataProviderInstance } from '../../../api/DataProvider';

export function useWaitingListData() {
  const {
    GetWaitingList,
    SetWaitingList,
    UpdateWaitingList,
    DeleteWaitingList,
    SetWaitingListOffer,
    SetAppointment,
    GetPatientById,
    GetProcedureById,
    GetResourceById,
    CompanyConfig,
    UserData,
  } = DataProviderInstance();

  const stableCompanyConfig = useMemo(() => CompanyConfig, [CompanyConfig?.Id]);

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const loadData = useCallback((params = {}) => {
    if (!stableCompanyConfig?.Id) return;

    setLoading(true);
    setError(null);

    const requestParams = {
      companyId: stableCompanyConfig.Id,
      page: params.page || 1,
      limit: params.limit || 50,
      includeAvailability: true,
      daysLimit: params.daysLimit || 30,
      ...params,
    };

    GetWaitingList(requestParams, (response) => {
      if (response.error) {
        setError(response.error);
        setPatients([]);
      } else {
        setPatients(response.waitingList || []);
        setPagination({
          page: response.pagination?.page || 1,
          pages: response.pagination?.pages || 1,
          total: response.pagination?.total || 0,
        });
      }
      setLoading(false);
    });
  }, [stableCompanyConfig, GetWaitingList]);

  // Carrega dados iniciais (não depende de loadData para evitar loop de re-renders
  // causado por referências instáveis de GetWaitingList no DataProvider)
  useEffect(() => {
    if (stableCompanyConfig?.Id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableCompanyConfig?.Id]);

  const addPatient = useCallback((data, callback) => {
    SetWaitingList(data, (response) => {
      if (response.success) loadData();
      callback?.(response);
    });
  }, [SetWaitingList, loadData]);

  const updatePatient = useCallback((data, callback) => {
    UpdateWaitingList({ ...data, updatedBy: UserData?.id }, (response) => {
      if (response.success) loadData();
      callback?.(response);
    });
  }, [UpdateWaitingList, UserData, loadData]);

  const deletePatient = useCallback((id, callback) => {
    DeleteWaitingList(id, (response) => {
      if (response.success) loadData();
      callback?.(response);
    });
  }, [DeleteWaitingList, loadData]);

  // Register an offer (slot offered to patient via WhatsApp)
  const registerOffer = useCallback((data, callback) => {
    SetWaitingListOffer(data, (response) => {
      callback?.(response);
    });
  }, [SetWaitingListOffer]);

  // Create appointment from waiting list
  const createAppointment = useCallback((data, callback) => {
    SetAppointment(data, (response) => {
      if (response?.status === 'SUCCESS') {
        // Mark waiting list item as SCHEDULED
        if (data.waitingListItemId) {
          updatePatient({ id: data.waitingListItemId, status: 'SCHEDULED' });
        }
      }
      callback?.(response);
    });
  }, [SetAppointment, updatePatient]);

  return {
    patients,
    loading,
    error,
    pagination,
    loadData,
    addPatient,
    updatePatient,
    deletePatient,
    registerOffer,
    createAppointment,
    companyConfig: stableCompanyConfig,
    userData: UserData,
    getPatientById: GetPatientById,
    getProcedureById: GetProcedureById,
    getResourceById: GetResourceById,
  };
}
